// Client-specific Firebase configuration
import { initializeApp, getApps, getApp } from 'firebase/app';
import type { FirebaseApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import type { Auth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import type { FirebaseStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import type { Functions } from 'firebase/functions';

// Firebase modules will be properly bundled by Vite

function requireEnv(name: string): string {
  const value = import.meta.env[name];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Missing required Firebase environment variable: ${name}`);
  }
  return value;
}

// Firebase configuration
const firebaseConfig = {
  apiKey: requireEnv('VITE_FIREBASE_API_KEY'),
  authDomain: requireEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: requireEnv('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: requireEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: requireEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: requireEnv('VITE_FIREBASE_APP_ID'),
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase for client
let app: FirebaseApp;
let auth: Auth | null;
let db: Firestore | null;
let storage: FirebaseStorage | null;
let functions: Functions | null;
const storageUrlCache = new Map<string, string>();
const pendingStorageUrlRequests = new Map<string, Promise<string | null>>();

try {
  console.log('Initializing Firebase app...');
  
  // Check if Firebase app is already initialized
  app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  console.log('Firebase app initialized successfully');

  console.log('Initializing Firebase services...');
  auth = getAuth(app);
  console.log('Auth service initialized');
  
  // Initialize Firestore
  console.log('Attempting to initialize Firestore...');
  db = getFirestore(app);
  console.log('Firestore service initialized successfully');
  
  storage = getStorage(app);
  console.log('Storage service initialized');
  
  functions = getFunctions(app);
  console.log('Functions service initialized');
  
  console.log('All Firebase services initialized successfully');
} catch (error) {
  console.error('Failed to initialize Firebase services:', error);
  console.error('Error details:', error);
  
  // More specific error handling
  if (error instanceof Error) {
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
  }
  
  // Initialize with null values as fallback
  auth = null;
  db = null;
  storage = null;
  functions = null;
}

// Helper function to get storage URL
export async function getStorageUrl(path: string | null | undefined): Promise<string | null> {
  if (!path || !storage) return null;

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const cachedUrl = storageUrlCache.get(path);
  if (cachedUrl) {
    return cachedUrl;
  }

  const pendingRequest = pendingStorageUrlRequests.get(path);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = (async () => {
    try {
      const imageRef = ref(storage, path);
      const url = await getDownloadURL(imageRef);
      storageUrlCache.set(path, url);
      return url;
    } catch (error) {
      if (!isFirebaseStorageObjectNotFound(error)) {
        console.error('Error getting storage URL:', error);
      }
      return null;
    } finally {
      pendingStorageUrlRequests.delete(path);
    }
  })();

  pendingStorageUrlRequests.set(path, request);
  return request;
}

function isFirebaseStorageObjectNotFound(error: unknown): boolean {
  return typeof error === 'object'
    && error !== null
    && 'code' in error
    && (error as { code?: unknown }).code === 'storage/object-not-found';
}

export { auth, db, storage, functions };
